import React, { useState, useEffect, useCallback } from 'react';

const mono = '"JetBrains Mono", monospace';
const CX = 300, CY = 300, R = 240;
const DEG = Math.PI / 180;
const S_LAT = 25.3 * DEG, S_LNG = 55.4 * DEG;

/* Orthographic projection centered on Sharjah — looks like a real 3D sphere */
function project(lat, lng) {
  const lr = lat * DEG, dl = lng * DEG - S_LNG;
  const cosC = Math.sin(S_LAT) * Math.sin(lr) + Math.cos(S_LAT) * Math.cos(lr) * Math.cos(dl);
  if (cosC < 0) return null; // behind the globe
  const x = CX + R * Math.cos(lr) * Math.sin(dl);
  const y = CY - R * (Math.cos(S_LAT) * Math.sin(lr) - Math.sin(S_LAT) * Math.cos(lr) * Math.cos(dl));
  return { x, y };
}

/* Generate smooth arc points between two projected locations */
function greatCircleArc(lat1, lng1, lat2, lng2, steps = 40) {
  const pts = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const lat = lat1 + (lat2 - lat1) * t;
    const lng = lng1 + (lng2 - lng1) * t;
    // Add altitude curve (arc lifts away from surface)
    const alt = 1 + Math.sin(t * Math.PI) * 0.08;
    const p = project(lat, lng);
    if (p) {
      const dx = p.x - CX, dy = p.y - CY;
      pts.push({ x: CX + dx * alt, y: CY + dy * alt });
    }
  }
  return pts;
}

/* Continent outlines — simplified coastline paths for a realistic look */
function ContinentPaths() {
  // Very simplified continent shapes projected onto the globe
  const continents = [
    // Africa (east coast visible)
    [[35, 10], [30, 32], [12, 42], [0, 40], [-5, 38], [-12, 35], [-25, 32], [-35, 20], [-34, 28], [-28, 35], [-15, 40], [-1, 42], [5, 45], [12, 44], [15, 40], [20, 38], [25, 35], [30, 33], [37, 12], [35, 10]],
    // Europe
    [[36, 0], [38, 5], [42, 3], [44, 8], [48, 2], [52, 5], [54, 10], [56, 12], [58, 15], [60, 20], [62, 25], [60, 30], [58, 28], [55, 22], [52, 20], [48, 15], [46, 12], [44, 15], [42, 20], [40, 25], [38, 22], [36, 15], [36, 0]],
    // Middle East / Arabia
    [[30, 35], [28, 38], [25, 40], [22, 42], [20, 45], [18, 48], [15, 50], [13, 45], [15, 42], [20, 38], [22, 35], [25, 34], [28, 34], [30, 35]],
    // South Asia
    [[30, 62], [28, 68], [25, 72], [22, 75], [18, 78], [15, 80], [10, 78], [8, 76], [10, 72], [12, 70], [18, 68], [22, 65], [25, 62], [28, 60], [30, 62]],
    // Central Asia
    [[42, 52], [44, 58], [46, 62], [48, 68], [50, 72], [52, 68], [50, 60], [48, 55], [45, 50], [42, 52]],
    // East Asia (partial)
    [[22, 100], [25, 105], [30, 108], [35, 110], [38, 115], [40, 118], [42, 120], [38, 122], [35, 120], [30, 118], [25, 115], [22, 110], [20, 108], [22, 100]],
  ];

  return continents.map((cont, ci) => {
    const pts = cont.map(([lat, lng]) => project(lat, lng)).filter(Boolean);
    if (pts.length < 4) return null;
    const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ') + 'Z';
    return <path key={ci} d={d} fill="rgba(59,130,246,0.06)" stroke="rgba(59,130,246,0.12)" strokeWidth="0.5" />;
  });
}

/* Grid lines — latitude and longitude */
function GridLines() {
  const lines = [];
  // Latitudes
  for (let lat = -60; lat <= 80; lat += 20) {
    const pts = [];
    for (let lng = -180; lng <= 180; lng += 3) {
      const p = project(lat, lng);
      if (p) pts.push(p);
    }
    if (pts.length > 5) {
      const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
      lines.push(<path key={`lat${lat}`} d={d} fill="none" stroke="rgba(59,130,246,0.07)" strokeWidth="0.4" />);
    }
  }
  // Longitudes
  for (let lng = -180; lng < 180; lng += 30) {
    const pts = [];
    for (let lat = -80; lat <= 80; lat += 3) {
      const p = project(lat, lng);
      if (p) pts.push(p);
    }
    if (pts.length > 5) {
      const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
      lines.push(<path key={`lng${lng}`} d={d} fill="none" stroke="rgba(59,130,246,0.07)" strokeWidth="0.4" />);
    }
  }
  return <>{lines}</>;
}

const DESTINATIONS = [
  { name: 'RUSSIA', lat: 55.7, lng: 37.6 },
  { name: 'BELGIUM', lat: 50.8, lng: 4.3 },
  { name: 'KAZAKHSTAN', lat: 51.1, lng: 71.4 },
  { name: 'AZERBAIJAN', lat: 40.4, lng: 49.8 },
  { name: 'UZBEKISTAN', lat: 41.3, lng: 69.2 },
  { name: 'PAKISTAN', lat: 30.3, lng: 69.3 },
  { name: 'HONG KONG', lat: 22.3, lng: 114.1 },
  { name: 'KENYA', lat: -1.3, lng: 36.8 },
];

function ShippingGlobe() {
  const [time, setTime] = useState(0);

  useEffect(() => {
    let frame;
    const tick = (ts) => { setTime(ts); frame = requestAnimationFrame(tick); };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  // Each route has its own cycle: draw 3s, hold 4s, fade 1.5s = 8.5s per route
  // Staggered start by 0.8s per route
  const routeCycle = 8500;
  const drawPhase = 3000;
  const holdPhase = 4000;
  const fadePhase = 1500;

  const sharjah = project(25.3, 55.4);

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: 560, aspectRatio: '1', margin: '0 auto' }}>
      <svg viewBox="0 0 600 600" style={{ width: '100%', height: '100%', display: 'block' }}>
        <defs>
          <radialGradient id="sg-bg">
            <stop offset="0%" stopColor="rgba(10,15,30,0.5)" />
            <stop offset="70%" stopColor="rgba(10,15,30,0.3)" />
            <stop offset="100%" stopColor="rgba(10,15,30,0.1)" />
          </radialGradient>
          <radialGradient id="sg-glow">
            <stop offset="80%" stopColor="transparent" />
            <stop offset="92%" stopColor="rgba(59,130,246,0.12)" />
            <stop offset="96%" stopColor="rgba(59,130,246,0.25)" />
            <stop offset="98%" stopColor="rgba(59,130,246,0.08)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          <radialGradient id="sg-atmosphere">
            <stop offset="85%" stopColor="transparent" />
            <stop offset="95%" stopColor="rgba(59,130,246,0.04)" />
            <stop offset="100%" stopColor="rgba(59,130,246,0.02)" />
          </radialGradient>
          <filter id="sg-blur">
            <feGaussianBlur stdDeviation="4" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <filter id="sg-blur-lg">
            <feGaussianBlur stdDeviation="10" />
          </filter>
          <clipPath id="sg-clip"><circle cx={CX} cy={CY} r={R} /></clipPath>
        </defs>

        {/* Atmosphere glow — large soft circle */}
        <circle cx={CX} cy={CY} r={R + 60} fill="url(#sg-atmosphere)" />

        {/* Blue ring glow (the prominent ring from the screenshot) */}
        <circle cx={CX} cy={CY} r={R + 4} fill="none" stroke="rgba(59,130,246,0.4)" strokeWidth="2" filter="url(#sg-blur)" />
        <circle cx={CX} cy={CY} r={R} fill="none" stroke="rgba(59,130,246,0.15)" strokeWidth="1" />

        {/* Globe sphere fill */}
        <circle cx={CX} cy={CY} r={R} fill="url(#sg-bg)" />

        {/* Everything inside the globe gets clipped */}
        <g clipPath="url(#sg-clip)">
          {/* Grid lines */}
          <GridLines />

          {/* Continent shapes */}
          <ContinentPaths />

          {/* Shipping routes */}
          {DESTINATIONS.map((dest, i) => {
            const destP = project(dest.lat, dest.lng);
            if (!destP || !sharjah) return null;

            const arcPts = greatCircleArc(25.3, 55.4, dest.lat, dest.lng, 50);
            if (arcPts.length < 5) return null;
            const fullPath = arcPts.map((p, j) => `${j === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

            // Per-route animation timing
            const stagger = i * 800;
            const elapsed = ((time + 99999) - stagger) % routeCycle;
            let routeProgress, routeOpacity;

            if (elapsed < drawPhase) {
              routeProgress = elapsed / drawPhase;
              routeOpacity = 1;
            } else if (elapsed < drawPhase + holdPhase) {
              routeProgress = 1;
              routeOpacity = 1;
            } else {
              routeProgress = 1;
              routeOpacity = 1 - (elapsed - drawPhase - holdPhase) / fadePhase;
            }

            const pathLength = 1200;
            const drawn = pathLength * routeProgress;

            // Traveling dot
            const dotIdx = Math.floor(routeProgress * (arcPts.length - 1));
            const dotPt = arcPts[Math.min(dotIdx, arcPts.length - 1)];
            const showTravelDot = routeProgress > 0.02 && routeProgress < 0.92;
            const arrived = routeProgress >= 0.92;

            return (
              <g key={dest.name} opacity={Math.max(0, routeOpacity)}>
                {/* Route trail — glowing line */}
                <path d={fullPath} fill="none" stroke="rgba(59,130,246,0.15)" strokeWidth="1.5"
                  strokeDasharray={pathLength} strokeDashoffset={pathLength - drawn} />
                {/* Brighter core of the drawn route */}
                <path d={fullPath} fill="none" stroke="rgba(96,165,250,0.4)" strokeWidth="0.8"
                  strokeDasharray={pathLength} strokeDashoffset={pathLength - drawn} />

                {/* Traveling bright dot */}
                {showTravelDot && (
                  <>
                    <circle cx={dotPt.x} cy={dotPt.y} r="6" fill="rgba(96,165,250,0.15)" filter="url(#sg-blur)" />
                    <circle cx={dotPt.x} cy={dotPt.y} r="2.5" fill="#93C5FD" />
                  </>
                )}

                {/* Destination marker */}
                {arrived && (
                  <>
                    <circle cx={destP.x} cy={destP.y} r="3" fill="none" stroke="rgba(16,185,129,0.5)" strokeWidth="0.8">
                      <animate attributeName="r" values="3;8;3" dur="2.5s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.5;0;0.5" dur="2.5s" repeatCount="indefinite" />
                    </circle>
                    <circle cx={destP.x} cy={destP.y} r="3" fill="#10B981" />
                    <text
                      x={destP.x + (destP.x > CX ? 9 : -9)}
                      y={destP.y + 3.5}
                      fill="rgba(250,250,248,0.55)"
                      fontSize="8.5"
                      fontFamily={mono}
                      letterSpacing="0.06em"
                      textAnchor={destP.x > CX ? 'start' : 'end'}
                    >
                      {dest.name}
                    </text>
                  </>
                )}
              </g>
            );
          })}
        </g>

        {/* Sharjah HQ — always on top, outside clip so it's never cut */}
        {sharjah && (
          <>
            {/* Glow */}
            <circle cx={sharjah.x} cy={sharjah.y} r="12" fill="rgba(239,68,68,0.08)" filter="url(#sg-blur-lg)" />
            {/* Pulse ring */}
            <circle cx={sharjah.x} cy={sharjah.y} r="7" fill="none" stroke="#EF4444" strokeWidth="1" opacity="0.2">
              <animate attributeName="r" values="7;18;7" dur="2.5s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.25;0;0.25" dur="2.5s" repeatCount="indefinite" />
            </circle>
            {/* Solid dot */}
            <circle cx={sharjah.x} cy={sharjah.y} r="5" fill="#EF4444" />
            <circle cx={sharjah.x} cy={sharjah.y} r="2" fill="#FCA5A5" />
            {/* Label */}
            <text x={sharjah.x} y={sharjah.y + 20} fill="rgba(239,68,68,0.6)" fontSize="8.5" fontFamily={mono} textAnchor="middle" letterSpacing="0.1em" fontWeight="600">
              SHARJAH · HQ
            </text>
          </>
        )}

        {/* Subtle sphere highlight (top-left) for 3D feel */}
        <circle cx={CX - 80} cy={CY - 80} r={R * 0.9} fill="none" stroke="rgba(255,255,255,0.015)" strokeWidth="80"
          clipPath="url(#sg-clip)" />
      </svg>
    </div>
  );
}

export default ShippingGlobe;
